// components/VaRCalculator.jsx
import React, { useState } from 'react';
import '../../styles/VaRCalculator.css';

export const VaRCalculator = ({ onCalculate, portfolioId }) => {
  const [varData, setVarData] = useState({
    confidenceLevel: 95,
    timeHorizon: 1,
    method: 'historical',
    lookbackPeriod: 252
  });

  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setVarData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleCalculate = async () => {
    const response = await onCalculate();
    
    // Mock VaR calculation results
    const baseVaR = 45000;
    const confidenceMultiplier = {
      95: 1.0,
      99: 1.5,
      99.5: 1.8
    }[varData.confidenceLevel] || 1.0;

    const calculatedVaR = baseVaR * confidenceMultiplier;
    const expectedShortfall = calculatedVaR * 1.38;
    const historicalVaR = calculatedVaR * 0.92;
    const monteCarloVaR = calculatedVaR * 1.12;

    setResult({
      ...response,
      timestamp: new Date().toLocaleTimeString(),
      var95: calculatedVaR,
      expectedShortfall,
      confidenceLevel: varData.confidenceLevel,
      timeHorizon: varData.timeHorizon,
      method: varData.method,
      historicalVaR,
      monteCarloVaR,
      portfolioValue: 1250000,
      varPercentage: (calculatedVaR / 1250000 * 100).toFixed(2),
      breakdown: [
        { asset: 'Equities', contribution: 65, amount: calculatedVaR * 0.65 },
        { asset: 'Bonds', contribution: 15, amount: calculatedVaR * 0.15 },
        { asset: 'Commodities', contribution: 10, amount: calculatedVaR * 0.10 },
        { asset: 'Other', contribution: 10, amount: calculatedVaR * 0.10 }
      ]
    });
  };

  return (
    <div className="panel var-calculator">
      <div className="panel-header">
        <h2>Value at Risk (VaR) Calculator</h2>
        <span className="api-endpoint">GET /rms/risk/var/{portfolioId}</span>
      </div>

      <div className="panel-content">
        <div className="var-controls">
          <div className="var-parameters">
            <h3>VaR Parameters</h3>
            
            <div className="parameter-group">
              <label htmlFor="confidenceLevel">Confidence Level</label>
              <div className="confidence-levels">
                {[95, 99, 99.5].map(level => (
                  <button
                    key={level}
                    type="button"
                    className={`confidence-btn ${varData.confidenceLevel === level ? 'active' : ''}`}
                    onClick={() => setVarData(prev => ({ ...prev, confidenceLevel: level }))}
                  >
                    {level}%
                  </button>
                ))}
              </div>
            </div>

            <div className="parameter-group">
              <label htmlFor="timeHorizon">Time Horizon (days)</label>
              <div className="time-horizons">
                {[1, 5, 10, 20].map(days => (
                  <button
                    key={days}
                    type="button"
                    className={`horizon-btn ${varData.timeHorizon === days ? 'active' : ''}`}
                    onClick={() => setVarData(prev => ({ ...prev, timeHorizon: days }))}
                  >
                    {days} day{days > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>

            <div className="parameter-group">
              <label htmlFor="method">Calculation Method</label>
              <select
                id="method"
                name="method"
                value={varData.method}
                onChange={handleChange}
              >
                <option value="historical">Historical Simulation</option>
                <option value="parametric">Parametric (Variance-Covariance)</option>
                <option value="monte-carlo">Monte Carlo Simulation</option>
              </select>
            </div>

            <div className="parameter-group">
              <label htmlFor="lookbackPeriod">
                Lookback Period: {varData.lookbackPeriod} days
              </label>
              <input
                type="range"
                id="lookbackPeriod"
                name="lookbackPeriod"
                min="30"
                max="500"
                step="10"
                value={varData.lookbackPeriod}
                onChange={handleChange}
              />
              <div className="range-labels">
                <span>30 days</span>
                <span>1 year (252)</span>
                <span>500 days</span>
              </div>
            </div>
          </div>

          <div className="var-preview">
            <h3>Quick Preview</h3>
            <div className="preview-card">
              <div className="preview-label">Portfolio ID</div>
              <div className="preview-value">{portfolioId}</div>
              
              <div className="preview-label">Confidence Level</div>
              <div className="preview-value">{varData.confidenceLevel}%</div>
              
              <div className="preview-label">Time Horizon</div>
              <div className="preview-value">{varData.timeHorizon} day(s)</div>
              
              <div className="preview-label">Method</div>
              <div className="preview-value">
                {varData.method === 'historical' ? 'Historical Simulation' : 
                 varData.method === 'parametric' ? 'Parametric' : 'Monte Carlo'}
              </div>
            </div>
          </div>
        </div>

        <div className="calculate-section">
          <button 
            className="btn btn-primary calculate-btn"
            onClick={handleCalculate}
          >
            📉 Calculate VaR
          </button>
          <p className="calculation-note">
            Calculates the maximum potential loss over a specified time period at a given confidence level
          </p>
        </div>

        {result && (
          <div className="var-results">
            <div className="results-header">
              <h3>VaR Calculation Results</h3>
              <span className="timestamp">{result.timestamp}</span>
            </div>

            <div className="var-metrics">
              <div className="var-metric-card main">
                <div className="var-metric-label">
                  VaR ({result.confidenceLevel}%, {result.timeHorizon} day)
                </div>
                <div className="var-metric-value danger">
                  ${result.var95.toLocaleString()}
                </div>
                <div className="var-metric-subtext">
                  {result.varPercentage}% of portfolio
                </div>
              </div>

              <div className="var-metric-card">
                <div className="var-metric-label">Expected Shortfall</div>
                <div className="var-metric-value danger">
                  ${result.expectedShortfall.toLocaleString()}
                </div>
                <div className="var-metric-subtext">
                  Average loss beyond VaR
                </div>
              </div>

              <div className="var-metric-card">
                <div className="var-metric-label">Portfolio Value</div>
                <div className="var-metric-value">
                  ${result.portfolioValue.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="method-comparison">
              <h4>Method Comparison</h4>
              <div className="comparison-chart">
                <div className="chart-bar">
                  <div className="bar-label">Historical</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill historical"
                      style={{ width: `${(result.historicalVaR / result.var95 * 100)}%` }}
                    />
                  </div>
                  <div className="bar-value">
                    ${result.historicalVaR.toLocaleString()}
                  </div>
                </div>
                
                <div className="chart-bar">
                  <div className="bar-label">Parametric</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill parametric"
                      style={{ width: `${100}%` }}
                    />
                  </div>
                  <div className="bar-value">
                    ${result.var95.toLocaleString()}
                  </div>
                </div>
                
                <div className="chart-bar">
                  <div className="bar-label">Monte Carlo</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill montecarlo"
                      style={{ width: `${(result.monteCarloVaR / result.var95 * 100)}%` }}
                    />
                  </div>
                  <div className="bar-value">
                    ${result.monteCarloVaR.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="var-breakdown">
              <h4>Risk Contribution by Asset Class</h4>
              <div className="breakdown-grid">
                {result.breakdown.map((item, index) => (
                  <div key={index} className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="asset-name">{item.asset}</span>
                      <span className="asset-contribution">{item.contribution}%</span>
                    </div>
                    <div className="breakdown-bar">
                      <div 
                        className="breakdown-fill"
                        style={{ width: `${item.contribution}%` }}
                      />
                    </div>
                    <div className="breakdown-value">
                      ${item.amount.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="risk-interpretation">
              <h4>Interpretation</h4>
              <div className="interpretation-content">
                <p>
                  With {result.confidenceLevel}% confidence, your portfolio is not expected to lose more than{' '}
                  <strong>${result.var95.toLocaleString()}</strong> over the next {result.timeHorizon} day{result.timeHorizon > 1 ? 's' : ''}.
                </p>
                <p>
                  The Expected Shortfall of <strong>${result.expectedShortfall.toLocaleString()}</strong> represents the 
                  average loss you could expect if losses exceed the VaR threshold.
                </p>
                <div className="risk-level">
                  <span className="risk-label">Risk Level:</span>
                  <span className={`risk-indicator ${result.varPercentage > 5 ? 'high' : result.varPercentage > 3 ? 'medium' : 'low'}`}>
                    {result.varPercentage > 5 ? 'High' : result.varPercentage > 3 ? 'Medium' : 'Low'}
                  </span>
                </div>
              </div>
            </div>

            <div className="actions">
              <button className="btn btn-primary">
                📊 View Detailed Report
              </button>
              <button className="btn btn-secondary">
                🔔 Set VaR Alert
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};